import { useState, useEffect } from "react"
import { Navigation } from "@/components/Navigation"
import { Footer } from "@/components/Footer"
import { ArrowRight, CalendarDays, MapPin } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Link } from "react-router-dom"
import { SEO } from "@/components/SEO"

const executives = [
    { name: "Vijay Simhachalam", role: "Director of Global Operations", img: "/vijay-simhachalam.png", link: "/contact" },
    { name: "Suhaib Ahmed", role: "Regional IT Sales Head - APAC/EMA", img: "/suhaib-ahmed.png", link: "https://www.linkedin.com/in/suhaib-ahmed-aa04a7b6/" },
    { name: "Santthosh Purmani", role: "CIO / CMO / IT Managing Director", img: "/IMG_6220.jpg", link: "https://lrix.bio/sp/" },
    { name: "Srini Ragipally", role: "CEO / Interim COO", img: "/Srinivas Ragipally.jpg", link: "https://lrix.bio/srini/" },
]

const upcomingEvents = [
    {
        label: "Featured",
        title: "HIMSS 2026",
        date: "March 9–12, 2026",
        location: "Las Vegas, NV",
        description: "Join RSA Tech Group at the world's largest health information and technology conference. Connect with our leadership team to explore the future of Healthcare Innovation.",
        link: "/events/rsa-himss-2026",
        image: "/himss-2026-banner.png",
    },
    {
        label: "Recap",
        title: "CES 2026",
        date: "Jan 6–9, 2026",
        location: "Las Vegas, NV",
        description: "RSA Tech Group engaged global leaders at the world's most influential technology event to shape intelligent, human-centered enterprise systems.",
        link: "/events/rsa-ces-2026",
        image: "/images/CES 2026 Images/IMG_6287.JPG",
    },
]

const stories = [
    {
        category: "FINTECH",
        title: "Autonomous Banking Core",
        desc: "Modernizing a Tier-1 bank's legacy mainframe to cloud-native microservices using GenAI agents.",
    },
    {
        category: "MANUFACTURING",
        title: "Predictive Factory Twin",
        desc: "Reducing downtime by 85% via a digital twin ecosystem driven by predictive sensory AI.",
    },
    {
        category: "HEALTHCARE",
        title: "Cognitive Patient Care",
        desc: "Accelerating diagnosis workflows by 3x with an intelligent NLP assistant for clinicians.",
    },
]

const faqs = [
    {
        q: "What events does RSA Tech Group attend?",
        a: "RSA Tech Group participates in major industry conferences including HIMSS, CES, AWS re:Invent, and other technology and healthcare events. We focus on events where we can connect with partners, customers, and industry leaders driving digital transformation.",
    },
    {
        q: "How can I schedule a meeting with RSA Tech Group at an event?",
        a: "You can reach out to our leadership team directly through the bio links on this page, or contact us via our website to schedule a dedicated meeting during any event we're attending.",
    },
    {
        q: "What topics does RSA Tech Group typically discuss at events?",
        a: "Our team discusses enterprise AI transformation, agentic workflows, cloud-native modernization, intelligent automation, and industry-specific solutions for healthcare, fintech, and manufacturing.",
    },
]

const Events = () => {
    const [openFaq, setOpenFaq] = useState<number | null>(null)
    const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 })

    useEffect(() => {
        const targetDate = new Date("2026-03-09T00:00:00").getTime()
        const tick = () => {
            const distance = targetDate - Date.now()
            if (distance < 0) return
            setTimeLeft({
                days: Math.floor(distance / (1000 * 60 * 60 * 24)),
                hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
                minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
                seconds: Math.floor((distance % (1000 * 60)) / 1000),
            })
        }
        tick()
        const interval = setInterval(tick, 1000)
        return () => clearInterval(interval)
    }, [])

    return (
        <div className="min-h-screen bg-white text-gray-900">
            <SEO
                title="Events - RSA Tech Group | Conferences & Industry Events"
                description="Discover where RSA Tech Group is next. From HIMSS to CES, connect with our leadership team at the industry's most influential conferences."
                image="/himss-2026-banner.png"
            />
            <Navigation />

            {/* ── Hero: HIMSS 2026 Featured - Navy / Image Split ── */}
            <section className="relative min-h-[600px] lg:min-h-[700px] flex pt-20">
                {/* Navy left block */}
                <div className="relative w-full lg:w-[55%] bg-[#1C2B6E] flex items-center">
                    {/* Orange top accent bar */}
                    <div className="absolute top-0 left-0 right-0 h-1 bg-[#FDA400]" />

                    <div className="w-full max-w-2xl ml-auto px-8 md:px-16 py-20 lg:py-28">
                        {/* Editorial eyebrow */}
                        <div className="flex items-center gap-3 mb-8">
                            <div className="h-px w-10 bg-[#FDA400]" />
                            <span className="text-[#FDA400] text-sm font-semibold tracking-widest uppercase">
                                Featured Event
                            </span>
                        </div>

                        <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-white leading-[1.05] mb-6">
                            HIMSS{" "}
                            <span className="text-[#FDA400]">2026</span>
                        </h1>

                        <p className="text-white/80 text-lg md:text-xl leading-relaxed mb-3">
                            March 9–12, 2026
                        </p>
                        <p className="text-white/60 text-base mb-10">
                            Las Vegas Convention Center, Las Vegas NV
                        </p>

                        <Link to="/events/rsa-himss-2026">
                            <Button className="bg-[#FDA400] hover:bg-[#e59500] text-[#1C2B6E] font-bold text-base px-8 py-6 rounded-lg shadow-lg transition-all hover:-translate-y-0.5">
                                Explore RSA at HIMSS 2026
                                <ArrowRight className="ml-2 w-4 h-4" />
                            </Button>
                        </Link>
                    </div>
                </div>

                {/* Countdown right block */}
                <div className="hidden lg:flex w-[45%] relative bg-[#0f1629] items-center justify-center">
                    <div className="absolute inset-0 bg-[url('/himss-2026-banner.png')] bg-cover bg-center opacity-20" />
                    <div className="relative z-10 flex flex-col items-center gap-8">
                        <span className="text-[#FDA400] text-sm font-bold uppercase tracking-[0.2em]">Countdown to HIMSS 2026</span>
                        <div className="flex gap-5">
                            {[
                                { label: "Days", value: timeLeft.days },
                                { label: "Hours", value: timeLeft.hours },
                                { label: "Mins", value: timeLeft.minutes },
                                { label: "Secs", value: timeLeft.seconds },
                            ].map((item, i) => (
                                <div key={i} className="flex flex-col items-center justify-center w-24 h-28 bg-white/[0.04] border border-white/[0.08] rounded-2xl backdrop-blur-sm">
                                    <span className="text-4xl font-black text-white mb-1">{String(item.value).padStart(2, '0')}</span>
                                    <span className="text-[10px] font-bold text-[#FDA400] uppercase tracking-[0.2em]">{item.label}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Mobile: countdown below hero text */}
                <div className="lg:hidden absolute bottom-0 left-0 right-0 bg-[#0f1629]/90 backdrop-blur-sm py-4 px-4 flex justify-center gap-3">
                    {[
                        { label: "Days", value: timeLeft.days },
                        { label: "Hrs", value: timeLeft.hours },
                        { label: "Min", value: timeLeft.minutes },
                        { label: "Sec", value: timeLeft.seconds },
                    ].map((item, i) => (
                        <div key={i} className="flex flex-col items-center justify-center w-16 h-20 bg-white/[0.04] border border-white/[0.08] rounded-xl">
                            <span className="text-2xl font-black text-white">{String(item.value).padStart(2, '0')}</span>
                            <span className="text-[8px] font-bold text-[#FDA400] uppercase tracking-wider">{item.label}</span>
                        </div>
                    ))}
                </div>
            </section>

            {/* ── All Events - Off-White ── */}
            <section className="py-24 bg-[#FAFAFA]">
                <div className="container px-6 md:px-8">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="h-px w-10 bg-[#FDA400]" />
                        <span className="text-[#FDA400] text-sm font-semibold tracking-widest uppercase">
                            Conferences
                        </span>
                    </div>
                    <h2 className="text-3xl md:text-5xl font-black text-gray-900 mb-4">
                        Our Events
                    </h2>
                    <p className="text-gray-500 text-lg mb-14 max-w-2xl">
                        RSA Tech Group is present at the industry's most influential conferences - connecting with leaders shaping the future of enterprise technology.
                    </p>

                    <div className="grid md:grid-cols-2 gap-8">
                        {upcomingEvents.map((event) => (
                            <Link key={event.title} to={event.link} className="group">
                                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md hover:-translate-y-1">
                                    <div className="aspect-[16/9] overflow-hidden bg-gray-100 relative">
                                        <img
                                            src={event.image}
                                            alt={event.title}
                                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                        />
                                        <span className="absolute top-4 left-4 bg-[#FDA400] text-[#1C2B6E] text-xs font-bold tracking-widest uppercase px-3 py-1.5 rounded-md">
                                            {event.label}
                                        </span>
                                    </div>
                                    <div className="p-6 md:p-8">
                                        <h3 className="text-2xl font-bold text-gray-900 mb-3">
                                            {event.title}
                                        </h3>
                                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-4">
                                            <span className="flex items-center gap-1.5">
                                                <CalendarDays className="w-4 h-4 text-[#FDA400]" />
                                                {event.date}
                                            </span>
                                            <span className="flex items-center gap-1.5">
                                                <MapPin className="w-4 h-4 text-[#FDA400]" />
                                                {event.location}
                                            </span>
                                        </div>
                                        <p className="text-gray-500 leading-relaxed mb-6">
                                            {event.description}
                                        </p>
                                        <span className="inline-flex items-center gap-2 text-[#1C2B6E] font-semibold group-hover:gap-3 transition-all">
                                            View Event Details <ArrowRight className="w-4 h-4" />
                                        </span>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── Meet Our Executives - White ── */}
            <section className="py-24 bg-white">
                <div className="container px-6 md:px-8">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="h-px w-10 bg-[#FDA400]" />
                        <span className="text-[#FDA400] text-sm font-semibold tracking-widest uppercase">
                            Leadership
                        </span>
                    </div>
                    <h2 className="text-3xl md:text-5xl font-black text-gray-900 mb-4">
                        Meet Our Executives
                    </h2>
                    <p className="text-gray-500 text-lg mb-14 max-w-2xl">
                        Connect with the leaders representing RSA Tech Group at industry events worldwide.
                    </p>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
                        {executives.map((person) => (
                            <a
                                key={person.name}
                                href={person.link}
                                {...(person.link.startsWith("http")
                                    ? { target: "_blank", rel: "noopener noreferrer" }
                                    : {})}
                                className="group"
                            >
                                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md hover:-translate-y-1">
                                    <div className="aspect-[4/5] overflow-hidden bg-gray-100">
                                        <img
                                            src={person.img}
                                            alt={person.name}
                                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                        />
                                    </div>
                                    <div className="p-5">
                                        <h3 className="font-bold text-gray-900 text-base">
                                            {person.name}
                                        </h3>
                                        <p className="text-gray-500 text-sm mt-1">
                                            {person.role}
                                        </p>
                                        <span className="inline-flex items-center gap-1 text-[#1C2B6E] text-sm font-semibold mt-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                            View Bio
                                            <ArrowRight className="w-3.5 h-3.5" />
                                        </span>
                                    </div>
                                </div>
                            </a>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── Podcast: "The Future of Intelligence" - Off-White ── */}
            <section className="py-24 bg-[#FAFAFA]">
                <div className="container px-6 md:px-8">
                    <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-center">
                        {/* Left: text + episode card */}
                        <div className="flex-1 w-full">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="h-px w-10 bg-[#FDA400]" />
                                <span className="text-[#FDA400] text-sm font-semibold tracking-widest uppercase">
                                    Podcast
                                </span>
                            </div>
                            <h2 className="text-3xl md:text-5xl font-black text-gray-900 mb-4">
                                The Future of{" "}
                                <span className="text-[#1C2B6E]">Intelligence</span>
                            </h2>
                            <p className="text-gray-500 text-lg mb-10 max-w-lg">
                                Deep conversations with the architects of the cognitive era. Exploring agentic workflows, AGI, and the new digital workforce.
                            </p>

                            {/* Episode card */}
                            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8">
                                <p className="text-[#FDA400] text-xs font-bold tracking-widest uppercase mb-2">
                                    Coming Q1 2026
                                </p>
                                <h3 className="text-xl font-bold text-gray-900 mb-3">
                                    Episode 1: The Agentic Shift
                                </h3>
                                <p className="text-gray-500 leading-relaxed mb-6">
                                    Our leadership team sits down with industry pioneers to discuss how autonomous agents are rewriting the rules of enterprise operations.
                                </p>
                                <Link to="/leadership-team">
                                    <Button className="bg-[#1C2B6E] hover:bg-[#15224f] text-white font-bold px-6 py-5 rounded-lg transition-all hover:-translate-y-0.5">
                                        Connect with Leadership Team
                                        <ArrowRight className="ml-2 w-4 h-4" />
                                    </Button>
                                </Link>
                            </div>
                        </div>

                        {/* Right: offset image with navy backdrop */}
                        <div className="flex-1 w-full relative">
                            <div className="absolute top-6 left-6 right-0 bottom-0 bg-[#1C2B6E] rounded-2xl" />
                            <img
                                src="https://images.unsplash.com/photo-1590602847861-f357a9332bbc?q=80&w=1000&auto=format&fit=crop"
                                alt="Podcast Studio"
                                className="relative rounded-2xl w-full aspect-[4/3] object-cover shadow-lg"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* ── Success Stories - White ── */}
            <section className="py-24 bg-white">
                <div className="container px-6 md:px-8">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="h-px w-10 bg-[#FDA400]" />
                        <span className="text-[#FDA400] text-sm font-semibold tracking-widest uppercase">
                            Case Studies
                        </span>
                    </div>
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-14 gap-4">
                        <h2 className="text-3xl md:text-5xl font-black text-gray-900">
                            Success Stories
                        </h2>
                        <Link
                            to="/contact"
                            className="text-[#1C2B6E] font-semibold text-sm flex items-center gap-1 hover:gap-2 transition-all"
                        >
                            Connect with us <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>

                    {/* Asymmetric grid: 1 large left + 2 stacked right */}
                    <div className="grid lg:grid-cols-2 gap-6">
                        {/* Large card */}
                        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden group hover:shadow-md transition-shadow h-full">
                            <div className="p-8 flex flex-col justify-between h-full">
                                <div>
                                    <div className="flex items-center gap-2 mb-6">
                                        <span className="w-2 h-2 rounded-full bg-[#FDA400]" />
                                        <span className="text-xs font-bold tracking-widest text-gray-400 uppercase">
                                            {stories[0].category}
                                        </span>
                                    </div>
                                    <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                                        {stories[0].title}
                                    </h3>
                                    <p className="text-gray-500 text-lg leading-relaxed mb-8">
                                        {stories[0].desc}
                                    </p>
                                </div>
                                <span className="inline-flex items-center gap-2 text-[#1C2B6E] font-semibold group-hover:gap-3 transition-all">
                                    Read Full Story <ArrowRight className="w-4 h-4" />
                                </span>
                            </div>
                        </div>

                        {/* 2 stacked cards on right */}
                        <div className="flex flex-col gap-6">
                            {stories.slice(1).map((story) => (
                                <div
                                    key={story.title}
                                    className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 group hover:shadow-md transition-shadow flex-1"
                                >
                                    <div className="flex items-center gap-2 mb-4">
                                        <span className="w-2 h-2 rounded-full bg-[#FDA400]" />
                                        <span className="text-xs font-bold tracking-widest text-gray-400 uppercase">
                                            {story.category}
                                        </span>
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                                        {story.title}
                                    </h3>
                                    <p className="text-gray-500 leading-relaxed mb-6">
                                        {story.desc}
                                    </p>
                                    <span className="inline-flex items-center gap-2 text-[#1C2B6E] font-semibold group-hover:gap-3 transition-all">
                                        Read Full Story <ArrowRight className="w-4 h-4" />
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ── FAQ - Navy ── */}
            <section className="py-24 bg-[#1C2B6E]">
                <div className="container px-6 md:px-8 max-w-3xl">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="h-px w-10 bg-[#FDA400]" />
                        <span className="text-[#FDA400] text-sm font-semibold tracking-widest uppercase">
                            FAQ
                        </span>
                    </div>
                    <h2 className="text-3xl md:text-4xl font-black text-white mb-12">
                        Frequently Asked Questions
                    </h2>

                    <div className="space-y-4">
                        {faqs.map((faq, i) => {
                            const isOpen = openFaq === i
                            return (
                                <div
                                    key={i}
                                    className="border border-white/10 rounded-xl overflow-hidden"
                                >
                                    <button
                                        onClick={() => setOpenFaq(isOpen ? null : i)}
                                        className="w-full flex items-center justify-between px-6 py-5 text-left text-white hover:bg-white/5 transition-colors"
                                    >
                                        <span className="font-semibold text-base pr-4">
                                            {faq.q}
                                        </span>
                                        <span
                                            className={`text-2xl text-[#FDA400] shrink-0 transition-transform duration-300 ${
                                                isOpen ? "rotate-45" : ""
                                            }`}
                                        >
                                            +
                                        </span>
                                    </button>
                                    {isOpen && (
                                        <div className="px-6 pb-5 text-white/70 leading-relaxed text-sm">
                                            {faq.a}
                                        </div>
                                    )}
                                </div>
                            )
                        })}
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    )
}

export default Events
